import React, { useEffect, useState } from 'react';
import { defineMessage, FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import {
  BodyTextMedium,
  HeadingText40,
  DeprecatedInlineMessage as InlineMessage,
  BodyTextSmall,
} from '@modernhealth/vital-components';
import { colors } from '@modernhealth/vital-foundation';

import { useTranslation } from 'src/shared/hooks/formatMessage';
import { Head } from 'src/shared/components/Head';
import { useLocale } from 'src/shared/intl/translations';
import { useTranslationDisclaimer } from 'src/shared/hooks/useTranslationDisclaimer';
import { CirclesInternationalBanner } from 'src/modules/circles/components/CirclesInternationalBanner';
import { memberRegistrationUrl } from 'src/shared/components/Navbar';

import { FeaturedTopicsRow } from '../components/FeaturedTopicsRow';
import { ExploreSeriesRow } from '../components/ExploreSeriesRow';

const title = defineMessage({ defaultMessage: 'Circles by Modern Health' });
const description = defineMessage({
  defaultMessage:
    'Live group sessions led by therapists and coaches. Learn, grow, and connect across topics that impact our day-to-day lives.',
});

const HomePage: React.FC = () => {
  const { $t } = useTranslation();
  const { locale } = useLocale();

  const [showTranslationDisclaimer, setShowTranslationDiscalimer] = useState(locale !== 'en-US');

  useEffect(() => {
    setShowTranslationDiscalimer(locale !== 'en-US');
  }, [locale]);

  const translationDisclaimer = useTranslationDisclaimer();
  return (
    <div>
      <Head>
        <title>{$t(title)}</title>
        <meta property="og:title" content={$t(title)} />
        <meta property="og:description" content={$t(description)} />
        <meta
          property="og:image"
          content="https://assets.joinmodernhealth.com/circles_illustrations/circles-og-image.jpg"
        />
      </Head>
      {/* ToDo: Add launchdarkly feature flag implementation to control this*/}
      {/* <Banner
        title={$t({ defaultMessage: 'Circles by Modern Health' })}
        description={$t({
          defaultMessage: 'Live community sessions, led by therapists & coaches',
        })}
        buttonTitle={$t({ defaultMessage: 'What are Circles?' })}
      /> */}
      <CirclesInternationalBanner
        title={$t({ defaultMessage: 'Welcome to Circles!' })}
        description={$t({
          defaultMessage:
            'Community spaces for us to learn, grow, and connect across topics that impact our day-to-day lives.',
        })}
        buttonTitle={$t({ defaultMessage: 'View upcoming circles' })}
      />
      {/* TODO: Update component to make it dismissable */}
      {/* TODO: Add a cookie to save dismiss setting */}
      {showTranslationDisclaimer && translationDisclaimer}
      <FeaturedTopicsRow />
      <ExploreSeriesRow />
      <InlineMessage alignment="center" message="">
        <HeadingText40 css={{ paddingTop: '23px' }} marginBottom={16}>
          {$t({ defaultMessage: 'Circles by Modern Health' })}
        </HeadingText40>
        <BodyTextMedium fontColor="gray65" centered marginBottom={32}>
          <FormattedMessage
            defaultMessage="If you are a Modern Health member, <sign-in-link>sign in</sign-in-link> to see our full library of "
            values={{
              'sign-in-link': (chunk: string) => (
                <a
                  target="_blank"
                  rel="noreferrer"
                  css={{ textDecoration: 'underline' }}
                  href={memberRegistrationUrl}
                >
                  {chunk}
                </a>
              ),
              'modern-health-link': (chunk: string) => (
                <a
                  target="_blank"
                  rel="noreferrer"
                  css={{ textDecoration: 'underline' }}
                  href="https://www.modernhealth.com/how-it-works"
                >
                  {chunk}
                </a>
              ),
            }}
          />
          <br />
          <p>
            {$t({ defaultMessage: 'Live and On-Demand Circles offered in multiple languages.' })}
          </p>
        </BodyTextMedium>
        <BodyTextSmall fontColor="gray65" css={{ paddingBottom: '23px' }}>
          <FormattedMessage
            defaultMessage="Learn more about <about-circles-link>Circles</about-circles-link> and <modern-health-link>Modern Health</modern-health-link>"
            values={{
              'about-circles-link': (chunk: string) => (
                <Link
                  to="/about-circles"
                  css={{ textDecoration: 'underline', color: colors.gray65 }}
                >
                  {chunk}
                </Link>
              ),
              'modern-health-link': (chunk: string) => (
                <a
                  target="_blank"
                  rel="noreferrer"
                  css={{ textDecoration: 'underline', color: colors.gray65 }}
                  href="https://www.modernhealth.com/how-it-works"
                >
                  {chunk}
                </a>
              ),
            }}
          />
        </BodyTextSmall>
      </InlineMessage>
    </div>
  );
};

export default HomePage;
